(function () {
  const csPdpBuyboxSelectors = {
    data: '[data-cs-pdp-buybox-data]',
    optionInput: '[data-cs-pdp-buybox-option-input]',
    sellingPlanInput: '[data-cs-pdp-buybox-selling-plan]',
    quantityInput: '[data-cs-pdp-buybox-quantity-input]',
    quantityDecrease: '[data-cs-pdp-buybox-quantity-decrease]',
    quantityIncrease: '[data-cs-pdp-buybox-quantity-increase]',
    submitPrice: '[data-cs-pdp-buybox-submit-price]',
    rating: '[data-cs-pdp-buybox-rating]',
    ratingSource: '[data-cs-pdp-buybox-rating-source]',
    ratingValue: '[data-cs-pdp-buybox-rating-value]',
    ratingCount: '[data-cs-pdp-buybox-rating-count]',
    ratingSummary: '.yotpo-sr-bottom-line-summary',
    ratingCountText: '.yotpo-sr-bottom-line-text'
  }

  const csPdpRatingLabel = /rated\s+([\d.]+)\s+out\s+of\s+([\d.]+)/i

  const csPdpReviewsTarget = '#cs-pdp-reviews'
  const csPdpTopBars =
    '.section-custom-announcement-bar, .announcement-bar, .site-header, .cs-pdp-sticky-atc.is-visible'

  class CsPdpBuybox extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController()

      const dataElement = this.querySelector(csPdpBuyboxSelectors.data)
      if (!dataElement) return

      this.data = JSON.parse(dataElement.textContent)
      this.optionInputs = Array.from(this.querySelectorAll(csPdpBuyboxSelectors.optionInput))
      this.sellingPlanInput = this.querySelector(csPdpBuyboxSelectors.sellingPlanInput)
      this.quantityInput = this.querySelector(csPdpBuyboxSelectors.quantityInput)
      this.quantityDecreaseButton = this.querySelector(csPdpBuyboxSelectors.quantityDecrease)
      this.quantityIncreaseButton = this.querySelector(csPdpBuyboxSelectors.quantityIncrease)
      this.submitPriceElement = this.querySelector(csPdpBuyboxSelectors.submitPrice)
      this.ratingElement = this.querySelector(csPdpBuyboxSelectors.rating)
      this.ratingSource = this.querySelector(csPdpBuyboxSelectors.ratingSource)
      this.ratingValueElement = this.querySelector(csPdpBuyboxSelectors.ratingValue)
      this.ratingCountElement = this.querySelector(csPdpBuyboxSelectors.ratingCount)

      const signal = this.abortController.signal

      this.optionInputs.forEach((input) => {
        input.addEventListener('change', () => this._onOptionChange(input), { signal })
      })

      if (this.quantityDecreaseButton) {
        this.quantityDecreaseButton.addEventListener(
          'click',
          () => this._setQuantity(this._getQuantity() - 1),
          { signal }
        )
      }

      if (this.quantityIncreaseButton) {
        this.quantityIncreaseButton.addEventListener(
          'click',
          () => this._setQuantity(this._getQuantity() + 1),
          { signal }
        )
      }

      if (this.quantityInput) {
        this.quantityInput.addEventListener('change', () => this._setQuantity(this._getQuantity()), {
          signal
        })
      }

      const checkedOption = this.optionInputs.find((input) => input.checked)
      if (checkedOption) this._onOptionChange(checkedOption)

      if (this.ratingElement && document.querySelector(csPdpReviewsTarget)) {
        this.ratingElement.classList.add('is-linked')
        this.ratingElement.addEventListener('click', () => this._scrollToReviews(), { signal })
      }

      if (this.ratingSource) this._watchRatingSource()

      this._updateQuantityButtons()
      this._updateSubmitPrice()
    }

    _watchRatingSource() {
      this._readRatingSource()
      if (typeof MutationObserver === 'undefined') return

      this.ratingObserver = new MutationObserver(() => this._readRatingSource())
      this.ratingObserver.observe(this.ratingSource, { childList: true, subtree: true })
    }

    _readRatingSource() {
      const summary = this.ratingSource.querySelector(csPdpBuyboxSelectors.ratingSummary)
      if (!summary) return

      const label = summary.getAttribute('aria-label') || ''
      const match = label.match(csPdpRatingLabel)
      const countText = summary.querySelector(csPdpBuyboxSelectors.ratingCountText)

      if (!match || !countText) return

      const countLabel = countText.textContent.trim()
      const total = parseInt(countLabel.replace(/[^\d]/g, ''), 10)

      if (!total) {
        this.ratingElement.classList.add('is-empty')
        return
      }

      const average = parseFloat(match[1]).toFixed(1)
      const scale = parseFloat(match[2]).toFixed(1)

      if (this.ratingValueElement) this.ratingValueElement.textContent = average + ' / ' + scale
      if (this.ratingCountElement) this.ratingCountElement.textContent = countLabel

      this.ratingElement.classList.remove('is-empty')
      this.ratingElement.classList.remove('is-pending')
      if (this.ratingObserver) this.ratingObserver.disconnect()
    }

    _scrollToReviews() {
      const target = document.querySelector(csPdpReviewsTarget)
      if (!target) return

      let inset = 0
      document.querySelectorAll(csPdpTopBars).forEach((bar) => {
        const position = window.getComputedStyle(bar).position
        if (position !== 'fixed' && position !== 'sticky') return

        const rect = bar.getBoundingClientRect()
        if (rect.bottom <= 0) return

        inset = Math.max(inset, rect.bottom)
      })

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

      window.scrollTo({
        top: target.getBoundingClientRect().top + window.scrollY - inset,
        behavior: reduceMotion ? 'auto' : 'smooth'
      })
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort()
      if (this.ratingObserver) this.ratingObserver.disconnect()
    }

    _onOptionChange(input) {
      if (this.sellingPlanInput) {
        this.sellingPlanInput.value = input.dataset.sellingPlanId || ''
      }
      this._updateSubmitPrice()
    }

    _getQuantity() {
      if (!this.quantityInput) return 1
      const parsed = parseInt(this.quantityInput.value, 10)
      return Number.isNaN(parsed) ? 1 : parsed
    }

    _getMinQuantity() {
      if (!this.quantityInput) return 1
      const parsed = parseInt(this.quantityInput.getAttribute('min'), 10)
      return Number.isNaN(parsed) ? 1 : parsed
    }

    _getMaxQuantity() {
      if (!this.quantityInput) return null
      const parsed = parseInt(this.quantityInput.getAttribute('max'), 10)
      return Number.isNaN(parsed) ? null : parsed
    }

    _setQuantity(nextQuantity) {
      const minQuantity = this._getMinQuantity()
      const maxQuantity = this._getMaxQuantity()

      let quantity = nextQuantity
      if (quantity < minQuantity) quantity = minQuantity
      if (maxQuantity !== null && quantity > maxQuantity) quantity = maxQuantity

      if (this.quantityInput) this.quantityInput.value = quantity

      this._updateQuantityButtons()
      this._updateSubmitPrice()
    }

    _updateQuantityButtons() {
      const quantity = this._getQuantity()
      const maxQuantity = this._getMaxQuantity()

      if (this.quantityDecreaseButton) {
        this.quantityDecreaseButton.disabled = quantity <= this._getMinQuantity()
      }

      if (this.quantityIncreaseButton) {
        this.quantityIncreaseButton.disabled = maxQuantity !== null && quantity >= maxQuantity
      }
    }

    _selectedUnitPrice() {
      const checkedOption = this.optionInputs.find((input) => input.checked)
      if (!checkedOption) return this.data.price
      const price = parseInt(checkedOption.dataset.unitPrice, 10)
      return Number.isNaN(price) ? this.data.price : price
    }

    _selectedPurchaseMultiplier() {
      const checkedOption = this.optionInputs.find((input) => input.checked)
      if (!checkedOption) return 1
      const multiplier = parseInt(checkedOption.dataset.purchaseMultiplier, 10)
      return Number.isNaN(multiplier) ? 1 : multiplier
    }

    _updateSubmitPrice() {
      if (!this.submitPriceElement) return
      const total =
        this._selectedUnitPrice() * this._selectedPurchaseMultiplier() * this._getQuantity()
      this.submitPriceElement.textContent = window.csPdpFormatMoney(total, this.data.moneyFormat)
    }
  }

  if (!customElements.get('cs-pdp-buybox')) {
    customElements.define('cs-pdp-buybox', CsPdpBuybox)
  }
})()
